import { getHotVideoDetail } from "~/api/task"
import { pollTask } from "~/common/tasks/pollTask"

type PollHotVideoStepOptions<TStep> = {
  isStale?: () => boolean
  taskId: string
  selectStep: (task: API.Task.HotVideoTask) => TStep | null | undefined
  isProcessing: (step: TStep) => boolean
  isFailed: (step: TStep) => boolean
  getErrorMessage: (step: TStep) => string
  timeoutMessage: string
}

type PollHotVideoTaskOptions = {
  getErrorMessage: (task: API.Task.HotVideoTask) => string
  isDone: (task: API.Task.HotVideoTask) => boolean
  isFailed: (task: API.Task.HotVideoTask) => boolean
  isStale?: () => boolean
  onTask?: (task: API.Task.HotVideoTask) => void
  taskId: string
  timeoutMessage: string
}

const HOT_VIDEO_TASK_POLL_INTERVAL = 5000
const HOT_VIDEO_TASK_MAX_ATTEMPTS = 800

export const getHotVideoTaskId = (task?: API.Task.HotVideoTask | null) => {
  if (!task) return ""

  return task.id || (task as { task_id?: string }).task_id || ""
}

export const getImageTaskResultUrl = (
  task?:
    | API.Task.ImageTaskInfo
    | API.Task.ImageTask
    | API.Task.WhiteImageInfo
    | API.Task.WhiteImageTask
    | null,
) => {
  return task?.result_urls?.[0] || ""
}

export const pollHotVideoTask = ({
  taskId,
  ...options
}: PollHotVideoTaskOptions): Promise<API.Task.HotVideoTask | undefined> => {
  return pollTask({
    ...options,
    fetchTask: async () => (await getHotVideoDetail({ task_id: taskId })).data,
    interval: HOT_VIDEO_TASK_POLL_INTERVAL,
    maxAttempts: HOT_VIDEO_TASK_MAX_ATTEMPTS,
  })
}

export const pollHotVideoStep = async <TStep>(
  options: PollHotVideoStepOptions<TStep>,
): Promise<TStep | undefined> => {
  const {
    getErrorMessage,
    isFailed,
    isProcessing,
    selectStep,
    isStale,
    taskId,
    timeoutMessage,
  } = options
  const task = await pollHotVideoTask({
    isStale,
    getErrorMessage: (nextTask) => {
      const step = selectStep(nextTask)

      return step ? getErrorMessage(step) : timeoutMessage
    },
    isDone: (nextTask) => {
      const step = selectStep(nextTask)

      return Boolean(step && !isProcessing(step) && !isFailed(step))
    },
    isFailed: (nextTask) => {
      const step = selectStep(nextTask)

      return Boolean(step && isFailed(step))
    },
    taskId,
    timeoutMessage,
  })

  return task ? selectStep(task) : undefined
}
